import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, InputAdornment } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import dayjs from 'dayjs';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import SectionCard from '../../../components/SectionCard';
import StatCard from '../../../components/StatCard';
import InvButton from '../../../components/InvButton';
import ConfirmDialog from '../../../components/ConfirmDialog';
import FilterToolbar from '../../../components/FilterToolbar';
import {
  getWardReturns,
  getWardReturn,
  getWardReturnItems,
  acknowledgeWardReturn,
  rejectWardReturn,
} from '../../../api/pharmacy';
import { mapList, mapWardReturn, mapWardReturnItem } from '../../../utils/pharmacyMappers';
import {
  buildWardReturnAcknowledge,
  validateWardReturnAcknowledge,
  formatWardReturnError,
  acknowledgeSuccessToast,
  acknowledgeWardReturnWithRetry,
  buildReversalPlan,
} from '../../../utils/wardReturnEntity';
import { getChargeEvents, cancelCharge, updateCharge } from '../../../../ipd-billing/billing-api';
import usePharmacyLoad from '../../../hooks/usePharmacyLoad';
import useInvRole from '../../../hooks/useInvRole';
import { INV_ACTIONS } from '../../../utils/inventoryRoles';
import useItemNames from '../../../hooks/useItemNames';
import { getStoreActorId } from '../../../hooks/useStoreScope';
import { WARD_RETURN_STATUS_LABELS } from '../../../constants';
import { IPD_COLORS } from '../../../theme';
import { IPD_FIELD_HEIGHT } from '../../../../ipd/utils/ipdFieldStyles';
import { fadeInSx } from '../pharmacyStyles';
import PharmacyTableState from '../PharmacyTableState';
import WardReturnsTable from '../components/WardReturnsTable';
import WardReturnAcknowledgeDialog from '../components/WardReturnAcknowledgeDialog';
import WardReturnDetailDialog from '../components/WardReturnDetailDialog';
import TablePaginationBar from '../components/TablePaginationBar';

const PAGE_LIMIT = 200;
const DEFAULT_ROWS_PER_PAGE = 25;

const EMPTY_QUERY = { search: '', status: '', range: '', from: '', to: '' };

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...['SUBMITTED', 'PARTIALLY_RETURNED', 'RETURNED', 'REJECTED', 'CANCELLED'].map((value) => ({
    value,
    label: WARD_RETURN_STATUS_LABELS[value] || value.replace(/_/g, ' '),
  })),
];

const DATE_RANGE_OPTIONS = [
  { value: '', label: 'All dates' },
  { value: 'TODAY', label: 'Today' },
  { value: '7D', label: 'Last 7 days' },
  { value: '30D', label: 'Last 30 days' },
  { value: 'CUSTOM', label: 'Custom range' },
];

/** Columns the free-text box scans. Medicine names live on the line-items endpoint, not the list payload. */
const SEARCH_FIELDS = ['returnId', 'patient_id', 'admission_id', 'ward'];

/** Triage order — a work inbox sorts by "needs action", not alphabetically. */
const STATUS_RANK = {
  SUBMITTED: 0,
  PARTIALLY_RETURNED: 1,
  RETURNED: 2,
  REJECTED: 3,
  CANCELLED: 4,
  DRAFT: 5,
};

function matchesSearch(row, term) {
  if (!term) return true;
  return SEARCH_FIELDS.some((field) => String(row[field] ?? '').toLowerCase().includes(term));
}

function dateBounds({ range, from, to }) {
  if (!range) return null;
  if (range === 'CUSTOM') {
    if (!from && !to) return null;
    return {
      start: from ? dayjs(from).startOf('day') : null,
      end: to ? dayjs(to).endOf('day') : null,
    };
  }
  const days = range === '7D' ? 6 : range === '30D' ? 29 : 0;
  return { start: dayjs().subtract(days, 'day').startOf('day'), end: dayjs().endOf('day') };
}

function matchesDate(row, bounds) {
  if (!bounds) return true;
  if (!row.date) return false;
  const d = dayjs(row.date);
  if (!d.isValid()) return false;
  if (bounds.start && d.isBefore(bounds.start)) return false;
  if (bounds.end && d.isAfter(bounds.end)) return false;
  return true;
}

function sortValue(row, key) {
  if (key === 'date') {
    if (!row.date) return 0;
    const d = dayjs(row.date);
    return d.isValid() ? d.valueOf() : 0;
  }
  if (key === 'status') return STATUS_RANK[row.status] ?? 99;
  return String(row[key] ?? '').toLowerCase();
}

function sortRows(rows, { key, dir }) {
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    if (av < bv) return -factor;
    if (av > bv) return factor;
    return 0;
  });
}

/** Only an admission-linked return has an IPD charge to reverse — ward-stock returns don't. */
function isPatientBillable(header) {
  const admission = header?.admission_id;
  return Boolean(admission) && admission !== '—';
}

/**
 * Reverse the patient's IPD billing for everything the pharmacy accepted back.
 * Runs after the acknowledge succeeded, so a failure here leaves stock credited
 * but the charge standing — the caller reports that plainly rather than pretending
 * the whole action failed. Steps run one at a time: these are money writes on the
 * same billing account and must not race each other.
 */
async function reverseIpdBilling(header, lines, decisions) {
  const events = await getChargeEvents(header.admission_id);
  const { steps, unmatched } = buildReversalPlan(lines, decisions, events);
  const reason = `Ward return ${header.returnId || header.id}`;
  for (const step of steps) {
    if (step.action === 'CANCEL') {
      await cancelCharge(header.admission_id, step.charge.id, reason);
    } else {
      await updateCharge(header.admission_id, step.charge.id, {
        quantity: step.quantity,
        remarks: reason,
      });
    }
  }
  return { reversed: steps.length, unmatched: unmatched.length };
}

/**
 * Summary tile that doubles as a status filter. StatCard is a plain Box, so the
 * wrapper is what makes the tile reachable and operable from the keyboard.
 */
function StatFilterCard({ label, value, icon, tone, loading, active, onClick }) {
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={active}
      aria-label={`${label}: ${value}. Filter the list by this status`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <StatCard label={label} value={value} icon={icon} tone={tone} loading={loading} onClick={onClick} />
    </Box>
  );
}

export default function WardReturnsTab() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const base = url.replace('/ward-returns', '');
  const { resolveItemId, getIssueUomCode } = useItemNames();

  const [query, setQuery] = useState(EMPTY_QUERY);
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const [viewRow, setViewRow] = useState(null);

  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actioning, setActioning] = useState(false);
  const { can } = useInvRole();
  const canAcknowledge = can(INV_ACTIONS.WARDRETURN_ACKNOWLEDGE);
  const canReject = can(INV_ACTIONS.WARDRETURN_REJECT);

  const [ackOpen, setAckOpen] = useState(false);
  const [ackHeader, setAckHeader] = useState(null);
  const [ackLines, setAckLines] = useState([]);
  const [ackDecisions, setAckDecisions] = useState({});
  const [ackLoading, setAckLoading] = useState(false);
  const [ackSubmitting, setAckSubmitting] = useState(false);
  const [ackReverseBilling, setAckReverseBilling] = useState(false);

  const [extraOffset, setExtraOffset] = useState(0);
  const [extraItems, setExtraItems] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const { data, loading, error, reload } = usePharmacyLoad(
    () => getWardReturns({ limit: PAGE_LIMIT, offset: 0 }),
    [],
  );

  const firstPage = useMemo(() => mapList(data, mapWardReturn).filter(Boolean), [data]);

  useEffect(() => {
    setExtraOffset(0);
    setExtraItems([]);
    setHasMore(Boolean(data?.hasMore));
  }, [data]);

  const items = useMemo(() => [...firstPage, ...extraItems], [firstPage, extraItems]);

  const counts = useMemo(() => ({
    pending: items.filter((i) => i.status === 'SUBMITTED').length,
    accepted: items.filter((i) => i.status === 'RETURNED' || i.status === 'PARTIALLY_RETURNED').length,
    rejected: items.filter((i) => i.status === 'REJECTED').length,
  }), [items]);

  const processedCount = counts.accepted + counts.rejected;
  const acceptanceRate = processedCount ? Math.round((counts.accepted / processedCount) * 100) : null;

  /**
   * SERVER-SIDE SWAP POINT — search, filter, sort and paging all run over the rows
   * already fetched, because the list endpoint takes only limit/offset and drops
   * non-pharmacy return types client-side (see getWardReturns in api/pharmacy),
   * which makes the server's own total wrong for this screen anyway. When the
   * endpoint gains query params, pass `query` and `sort` into getWardReturns and
   * delete the three memos below — everything downstream reads `visibleRows` and
   * `sorted.length`, so nothing else has to change.
   */
  const filtered = useMemo(() => {
    const term = query.search.trim().toLowerCase();
    const bounds = dateBounds(query);
    return items.filter((row) => (
      matchesSearch(row, term)
      && (!query.status || row.status === query.status)
      && matchesDate(row, bounds)
    ));
  }, [items, query]);

  const sorted = useMemo(() => sortRows(filtered, sort), [filtered, sort]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const safePage = Math.min(page, pageCount);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const visibleRows = useMemo(() => {
    const start = (safePage - 1) * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, safePage, rowsPerPage]);

  const patchQuery = useCallback((patch) => {
    setQuery((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setQuery(EMPTY_QUERY);
    setPage(1);
  }, []);

  const handleSort = useCallback((key) => {
    setSort((prev) => (prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'date' ? 'desc' : 'asc' }));
    setPage(1);
  }, []);

  const filtersActive = Boolean(query.search || query.status || query.range);

  const loadMore = useCallback(async () => {
    const nextOffset = extraOffset + PAGE_LIMIT;
    setLoadingMore(true);
    try {
      const nextPage = await getWardReturns({ limit: PAGE_LIMIT, offset: nextOffset });
      const mapped = mapList(nextPage, mapWardReturn).filter(Boolean);
      setExtraItems((prev) => [...prev, ...mapped]);
      setExtraOffset(nextOffset);
      setHasMore(Boolean(nextPage?.hasMore));
    } catch (e) {
      toast.error(formatWardReturnError(e, 'Failed to load more returns'));
    } finally {
      setLoadingMore(false);
    }
  }, [extraOffset]);

  const closeAck = useCallback((force = false) => {
    if (ackSubmitting && !force) return;
    setAckOpen(false);
    setAckHeader(null);
    setAckLines([]);
    setAckDecisions({});
    setAckReverseBilling(false);
  }, [ackSubmitting]);

  async function openAcknowledge(row) {
    setAckOpen(true);
    setAckLoading(true);
    setAckHeader(row);
    setAckLines([]);
    setAckDecisions({});
    setAckReverseBilling(false);
    try {
      const [header, itemsPayload] = await Promise.all([
        getWardReturn(row.id),
        getWardReturnItems(row.id),
      ]);
      const mappedHeader = mapWardReturn(header) || row;
      const lines = (Array.isArray(itemsPayload) ? itemsPayload : [])
        .map(mapWardReturnItem)
        .filter(Boolean);
      const decisions = {};
      lines.forEach((line) => {
        decisions[line.id] = { accepted: line.requested_quantity, reason: '' };
      });
      setAckHeader(mappedHeader);
      setAckLines(lines);
      setAckDecisions(decisions);
      setAckReverseBilling(isPatientBillable(mappedHeader));
    } catch (e) {
      toast.error(formatWardReturnError(e, 'Failed to load return details'));
      setAckOpen(false);
    } finally {
      setAckLoading(false);
    }
  }

  function setAccepted(lineId, requested, raw) {
    const n = Number(raw);
    let accepted = Number.isFinite(n) ? n : 0;
    if (accepted < 0) accepted = 0;
    if (accepted > requested) accepted = requested;
    setAckDecisions((prev) => ({
      ...prev,
      [lineId]: { ...prev[lineId], accepted, reason: prev[lineId]?.reason || '' },
    }));
  }

  function setReason(lineId, reason) {
    setAckDecisions((prev) => ({
      ...prev,
      [lineId]: { ...prev[lineId], accepted: prev[lineId]?.accepted ?? 0, reason },
    }));
  }

  function acceptAll() {
    setAckDecisions((prev) => {
      const next = { ...prev };
      ackLines.forEach((line) => {
        next[line.id] = { accepted: line.requested_quantity, reason: '' };
      });
      return next;
    });
  }

  function rejectAllLines() {
    setAckDecisions((prev) => {
      const next = { ...prev };
      ackLines.forEach((line) => {
        next[line.id] = { accepted: 0, reason: prev[line.id]?.reason || '' };
      });
      return next;
    });
  }

  async function submitAcknowledge() {
    if (!ackHeader?.id) return;
    if (!canAcknowledge) {
      toast.error('You do not have permission to acknowledge ward returns');
      return;
    }
    const reverseBilling = ackReverseBilling && isPatientBillable(ackHeader);
    const body = buildWardReturnAcknowledge(ackLines, ackDecisions, getStoreActorId());
    const errs = validateWardReturnAcknowledge(ackLines, body);
    if (errs.length) {
      toast.error(errs[0]);
      return;
    }
    setAckSubmitting(true);
    try {
      const updated = await acknowledgeWardReturnWithRetry(() =>
        acknowledgeWardReturn(ackHeader.id, body),
      );
      toast.success(acknowledgeSuccessToast(updated?.status));
      if (reverseBilling) {
        try {
          const { reversed, unmatched } = await reverseIpdBilling(ackHeader, ackLines, ackDecisions);
          if (reversed) toast.success(`Billing reversed for ${reversed} item${reversed > 1 ? 's' : ''}`);
          if (unmatched) {
            toast.warn(`${unmatched} item${unmatched > 1 ? 's' : ''} had no matching charge — reverse in billing manually`);
          }
          if (!reversed && !unmatched) toast.info('Nothing accepted — no billing to reverse');
        } catch (e) {
          toast.error(`Stock credited, but billing reversal failed — ${formatWardReturnError(e, 'reverse it manually')}`);
        }
      }
      setAckSubmitting(false);
      closeAck(true);
      reload();
    } catch (e) {
      toast.error(formatWardReturnError(e, 'Failed to acknowledge return'));
      setAckSubmitting(false);
    }
  }

  async function submitReject() {
    if (!rejectDialog?.id) return;
    if (!canReject) {
      toast.error('You do not have permission to reject ward returns');
      return;
    }
    setActioning(true);
    try {
      await rejectWardReturn(rejectDialog.id, {
        rejected_by: getStoreActorId() || null,
        reason: rejectReason.trim() || null,
      });
      toast.success('Ward return rejected');
      setRejectDialog(null);
      setRejectReason('');
      reload();
    } catch (e) {
      toast.error(formatWardReturnError(e, 'Rejection failed — refresh before trying again'));
    } finally {
      setActioning(false);
    }
  }

  function openReject(row) {
    setRejectReason('');
    setRejectDialog(row);
  }

  return (
    <Box sx={fadeInSx}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
        gap: 2,
        mb: 2.5,
      }}>
        <StatFilterCard
          label="Pending Returns"
          value={counts.pending}
          icon={PendingActionsIcon}
          tone="amber"
          loading={loading}
          active={query.status === 'SUBMITTED'}
          onClick={() => patchQuery({ status: query.status === 'SUBMITTED' ? '' : 'SUBMITTED' })}
        />
        <StatFilterCard
          label="Accepted"
          value={counts.accepted}
          icon={CheckCircleOutlineIcon}
          tone="mint"
          loading={loading}
          active={query.status === 'RETURNED'}
          onClick={() => patchQuery({ status: query.status === 'RETURNED' ? '' : 'RETURNED' })}
        />
        <StatFilterCard
          label="Rejected"
          value={counts.rejected}
          icon={HighlightOffIcon}
          tone="red"
          loading={loading}
          active={query.status === 'REJECTED'}
          onClick={() => patchQuery({ status: query.status === 'REJECTED' ? '' : 'REJECTED' })}
        />
        <StatFilterCard
          label="Total Returns"
          value={items.length}
          icon={AssignmentReturnIcon}
          tone="teal"
          loading={loading}
          active={!query.status}
          onClick={() => patchQuery({ status: '' })}
        />
      </Box>

      <FilterToolbar>
        <FilterToolbar.Search
          value={query.search}
          onChange={(e) => patchQuery({ search: e.target.value })}
          placeholder="Search by Return ID, Patient ID, Admission No, Ward…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: IPD_COLORS.textSecondary, fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: query.search ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => patchQuery({ search: '' })}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <FilterToolbar.Filter
          label="Status"
          width="wide"
          value={query.status}
          onChange={(e) => patchQuery({ status: e.target.value })}
          options={STATUS_OPTIONS}
        />
        <FilterToolbar.Filter
          label="Date"
          value={query.range}
          onChange={(e) => patchQuery({ range: e.target.value, from: '', to: '' })}
          options={DATE_RANGE_OPTIONS}
        />
        {query.range === 'CUSTOM' && (
          <>
            <FilterToolbar.DateFilter
              label="From"
              value={query.from}
              onChange={(e) => patchQuery({ from: e.target.value })}
            />
            <FilterToolbar.DateFilter
              label="To"
              value={query.to}
              onChange={(e) => patchQuery({ to: e.target.value })}
            />
          </>
        )}
        <FilterToolbar.Spacer />
        <FilterToolbar.Actions>
          <InvButton
            tone="teal"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => history.push(`${base}/return/new`)}
            sx={{ height: IPD_FIELD_HEIGHT }}
          >
            Patient Return
          </InvButton>
        </FilterToolbar.Actions>
      </FilterToolbar>

      <SectionCard
        title="Ward Return Requests"
        subtitle={acceptanceRate == null
          ? 'Medicines raised for return from wards'
          : `Medicines raised for return from wards · ${acceptanceRate}% of processed returns accepted`}
      >
        <PharmacyTableState
          loading={loading}
          error={error}
          empty={!loading && !error && sorted.length === 0}
          emptyIcon={filtersActive ? SearchOffIcon : AssignmentReturnIcon}
          emptyTitle={filtersActive ? 'No Matching Ward Returns' : 'No Ward Return Requests Found'}
          emptyMessage={filtersActive
            ? 'No returns match the current search and filters. Try widening the date range or clearing the filters.'
            : 'Returns raised by the wards will appear here for the pharmacy to acknowledge or reject.'}
          emptyAction={filtersActive ? (
            <InvButton
              variant="outlined"
              tone="teal"
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={resetFilters}
            >
              Clear filters
            </InvButton>
          ) : null}
          skeleton={(
            <WardReturnsTable
              loading
              sort={sort}
              onSortChange={handleSort}
              skeletonRows={Math.min(rowsPerPage, 8)}
            />
          )}
          onRetry={reload}
        >
          <>
            <WardReturnsTable
              rows={visibleRows}
              sort={sort}
              onSortChange={handleSort}
              onView={setViewRow}
              onAcknowledge={canAcknowledge ? openAcknowledge : undefined}
              onReject={canReject ? openReject : undefined}
              acknowledgeDisabled={!canAcknowledge || ackLoading || ackSubmitting}
              rejectDisabled={!canReject || actioning}
            />
            <TablePaginationBar
              page={safePage}
              rowsPerPage={rowsPerPage}
              total={sorted.length}
              onPageChange={setPage}
              onRowsPerPageChange={(n) => {
                setRowsPerPage(n);
                setPage(1);
              }}
              hasMore={hasMore}
              onLoadMore={loadMore}
              loadingMore={loadingMore}
            />
          </>
        </PharmacyTableState>
      </SectionCard>

      <WardReturnDetailDialog
        open={Boolean(viewRow)}
        row={viewRow}
        onClose={() => setViewRow(null)}
        resolveItemId={resolveItemId}
        resolveUomCode={getIssueUomCode}
      />

      <ConfirmDialog
        open={Boolean(rejectDialog)}
        title="Reject Ward Return"
        description={
          rejectDialog
            ? `Refuse return ${rejectDialog.returnId} from ${
              rejectDialog.ward && rejectDialog.ward !== '—'
                ? `ward ${rejectDialog.ward}`
                : 'an unassigned ward'
            }? No stock will be credited.`
            : ''
        }
        destructive
        optionalReason
        reasonLabel="Reason (optional)"
        reasonValue={rejectReason}
        onReasonChange={setRejectReason}
        confirmLabel="Reject"
        loading={actioning}
        onConfirm={submitReject}
        onCancel={() => {
          if (actioning) return;
          setRejectDialog(null);
          setRejectReason('');
        }}
      />

      <WardReturnAcknowledgeDialog
        resolveUomCode={getIssueUomCode}
        open={ackOpen}
        loading={ackLoading}
        submitting={ackSubmitting}
        header={ackHeader}
        lines={ackLines}
        decisions={ackDecisions}
        reverseBilling={ackReverseBilling}
        billable={isPatientBillable(ackHeader)}
        resolveItemId={resolveItemId}
        onClose={() => closeAck()}
        onSubmit={submitAcknowledge}
        onAcceptedChange={setAccepted}
        onReasonChange={setReason}
        onAcceptAll={acceptAll}
        onRejectAll={rejectAllLines}
        onReverseBillingChange={setAckReverseBilling}
      />
    </Box>
  );
}
